import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils.js';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Extra props on top of Radix's `DialogContent`:
 *
 *   - `preventDismissOnInteractOutside`: when true, clicking the overlay
 *     and pressing Escape are silently swallowed. Use for wizards where
 *     accidental dismissal would lose multi-step state — the operator
 *     must explicitly hit Cancel / X / a footer button to close.
 *
 * The X close button still works in all cases; consumers who want a
 * "discard your changes?" prompt should add the confirm inside the
 * top-level `<Dialog onOpenChange={...}>` handler (it fires for X +
 * any explicit `setOpen(false)` and is the right place to gate close).
 */
interface DialogContentExtraProps {
  preventDismissOnInteractOutside?: boolean;
}

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & DialogContentExtraProps
>(({ className, children, preventDismissOnInteractOutside, ...props }, ref) => {
  // When the caller wires its own `onInteractOutside` / `onEscapeKeyDown`,
  // honour their handler first; only call preventDefault if they didn't
  // already. Keeps the opt-out non-destructive for future bespoke uses.
  const onInteractOutside = (e: Event): void => {
    props.onInteractOutside?.(e as Parameters<NonNullable<typeof props.onInteractOutside>>[0]);
    if (preventDismissOnInteractOutside && !e.defaultPrevented) e.preventDefault();
  };
  const onEscapeKeyDown = (e: KeyboardEvent): void => {
    props.onEscapeKeyDown?.(e as Parameters<NonNullable<typeof props.onEscapeKeyDown>>[0]);
    if (preventDismissOnInteractOutside && !e.defaultPrevented) e.preventDefault();
  };
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Asymmetric horizontal padding: `pr-10` (40px) reserves space for
          // the absolute-positioned X close button (which sits at right-4 +
          // is 16px wide, so occupies x=16-32 from the right edge — content
          // ending at x=40 from right gives an 8px gap from the X). Left
          // stays at `pl-6` (24px) for normal indent. The alternative —
          // padding the header only — created a visible right-edge "step"
          // when form fields below the header extended further right than
          // the header text; uniform right-padding here keeps all dialog
          // children aligned at the same right edge.
          //
          // `grid-cols-1` is load-bearing: CSS grid columns default to
          // `auto` which means each column expands to its content's
          // intrinsic width — long unbreakable child content (a typed
          // PAT, a long helper paragraph) can then push the dialog
          // sideways past `max-w-lg/xl`. `grid-cols-1` resolves to
          // `repeat(1, minmax(0, 1fr))` whose `min: 0` lets the column
          // shrink below content width so the dialog stays contained.
          'fixed left-1/2 top-1/2 z-50 grid grid-cols-1 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 ' +
            'border border-border bg-card pl-6 pr-10 py-6 shadow-lg sm:rounded-lg ' +
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
          className,
        )}
        {...props}
        onInteractOutside={onInteractOutside}
        onEscapeKeyDown={onEscapeKeyDown}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Plain flex column with tight spacing. X-close clearance is owned by
 * DialogContent's asymmetric padding, not here — that keeps the right
 * edges of the header and the form/body content aligned.
 */
export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn('flex flex-col space-y-1.5', className)} {...props} />;
}

/**
 * `sm:flex-wrap` is defensive — when the sum of button widths
 * exceeds the dialog content area at the `sm:` row layout, the
 * row wraps onto a second line instead of forcing the footer
 * wider than the dialog. Without it, a long primary CTA + a
 * long secondary action push the modal sideways.
 */
export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end', className)}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
